// ============================================================
// ENTRY POINT — Re-exports from organized data files
// All data is now organized by navbar sections in:
//   src/data/home.js            → Home page sections
//   src/data/pages.js           → Programmes, Universities, Companies, Careers sections
//   src/data/leadership-data.js → About / Leadership page sections
// ============================================================

// Logos
export const LOGO_PNG = `${process.env.PUBLIC_URL}/ai_prof.png`;
export const LOGO_GIF = `${process.env.PUBLIC_URL}/ai_prof.gif`;

// ============================================================
// HOME SECTION — Shared data arrays used by HomePage components
// ============================================================

export const AUDIENCE_CARDS = [
    {
        key: "student",
        tag: "For Students",
        headline: "Your Degree. Supercharged with AI.",
        subcopy:
            "Build real projects. Earn verified skills. Get placed at companies that pay you what you're worth.",
        cta: "Join the Student Waitlist",
        accent: "from-aiblue to-blue-700",
        image: "https://images.unsplash.com/photo-1758270705172-07b53627dfcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
    {
        key: "university",
        tag: "For Universities",
        headline: "Give your students the edge no traditional degree can.",
        subcopy:
            "AI Prof integrates into your existing degree programme no disruption, no overhaul. Just better outcomes. More placements. Happier students. Stronger institutional reputation.",
        cta: "Partner With AI Prof",
        accent: "from-aiorange to-orange-600",
        image: `${process.env.PUBLIC_URL}/images/image.png`,
    },
    {
        key: "company",
        tag: "For Companies",
        headline: "Hire Smarter. Hire Faster. Hire AI-Ready.",
        subcopy:
            "Stop sifting through CVs. AI Prof sends you pre-verified, project-backed candidates matched to your exact requirements.",
        cta: "Start Hiring From AI Prof",
        accent: "from-emerald-500 to-teal-600",
        image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
];

export const JOURNEY_STEPS = [
    {
        n: "01",
        title: "Discover Your Passion",
        desc: "Take AI Prof's AI Readiness Quiz. Get a personalised domain recommendation and career roadmap in minutes.",
        image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
    {
        n: "02",
        title: "Learn with AI by Your Side",
        desc: "Domain-specific courses + Veda, your personal AI tutor available 24/7. Live sessions led by working engineers.",
        image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
    {
        n: "03",
        title: "Build What Companies Ask For",
        desc: "Every module ends with a real-world project deployed, documented, and added to your verified portfolio.",
        image: `${process.env.PUBLIC_URL}/general/learnWithAI.png`,
    },
    {
        n: "04",
        title: "Everything You Achieve, All in One Place",
        desc: "Keep your skills, projects, and certifications live and accessible for mentors, universities and companies that hires you at anytime.",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
    {
        n: "05",
        title: "Get Hired",
        desc: "We connect companies with your verified portfolio so your skills, projects, and achievements get noticed by the right employers.",
        image: `${process.env.PUBLIC_URL}/general/placed.png`,
    },
];

export const COMPARISON_ROWS = [
    [
        "Curriculum",
        "Outdated textbooks",
        "Generic content",
        "AI-integrated, updated every semester",
    ],
    [
        "Learning Style",
        "Lectures & rote memorisation",
        "Pre-recorded videos",
        "Build real projects from Semester 1",
    ],
    [
        "Mentorship",
        "1 professor : 100 students",
        "Limited Q&A forums",
        "Veda AI tutor — 24/7 personal",
    ],
    [
        "Assessment",
        "MCQs the night before",
        "Auto-graded quizzes",
        "AI dialogue-based, tests real understanding",
    ],
    [
        "Portfolio",
        "A certificate",
        "Course completion badge",
        "Verified portfolio + Tech Readiness Score",
    ],
    [
        "Career Support",
        "Final year only",
        "Self-serve job board",
        "Career roadmap from Semester 1",
    ],
    [
        "Hiring Connect",
        "Hit-or-miss campus drives",
        "Apply yourself",
        "Portfolio auto-matched to companies",
    ],
];

export const COURSES = [
    {
        key: "fullstack",
        name: "Full Stack Web Development",
        tagline: "From idea to deployed product.",
        tools: ["React", "Node.js", "MongoDB", "AWS", "Docker", "GitHub"],
        outcome: "Build 5 production-grade projects.",
        level: "Beginner → Job-Ready",
        domain: "Full Stack",
        format: "Hybrid",
        duration: "24 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "ai",
        name: "AI & Machine Learning",
        tagline: "Don't just use AI. Build it.",
        tools: ["Python", "TensorFlow", "PyTorch", "HuggingFace", "LangChain"],
        outcome: "Build end-to-end ML models.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Live + Recorded",
        duration: "20 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "data",
        name: "Data Science & Analytics",
        tagline: "Turn raw data into decisions.",
        tools: ["Python", "SQL", "Pandas", "Power BI", "Tableau"],
        outcome: "Build a data portfolio companies review.",
        level: "Beginner → Intermediate",
        domain: "Data Science",
        format: "Hybrid",
        duration: "18 weeks",
        bg: "bg-aimint",
    },
    {
        key: "cloud",
        name: "Cloud Engineering & DevOps",
        tagline: "Make every product run perfectly.",
        tools: ["AWS", "Kubernetes", "Terraform", "CI/CD", "Linux"],
        outcome: "Earn cloud certifications. Deploy real infra.",
        level: "Intermediate",
        domain: "Cloud",
        format: "Live + Recorded",
        duration: "20 weeks",
        bg: "bg-aisky",
    },
    {
        key: "cyber",
        name: "Cybersecurity & Ethical Hacking",
        tagline: "Protect what others build.",
        tools: ["Kali Linux", "Wireshark", "Metasploit", "OWASP"],
        outcome: "Earn CEH-aligned certifications.",
        level: "Beginner → Advanced",
        domain: "Cybersecurity",
        format: "Hybrid",
        duration: "22 weeks",
        bg: "bg-aibeige",
    },
];

export const UNI_FEATURES = [
    {
        title: "Curated Multimedia Content",
        desc: "Videos, audio, and notes for every topic — assembled by AI, not copied from a textbook written years ago.",
    },
    {
        title: "Industry Use-Case Projects",
        desc: "Every project based on a real problem from a real industry. Students don't just submit work — they build proof.",
    },
    {
        title: "Applied Delivery Workflows",
        desc: "Subjects taught using the same tools professionals use — GitHub, cloud platforms, live data, AI systems.",
    },
    {
        title: "Integrated Code Playground",
        desc: "The moment something is taught, your student writes and runs real code — instantly, in the same class.",
    },
    {
        title: "Veda AI Agent — 24/7 Tutor",
        desc: "Stuck at 11pm before an exam? Veda answers in seconds. Every day, every hour, no limits.",
    },
    {
        title: "Automated Evaluation",
        desc: "Instant, detailed feedback — not 3 weeks later. Students improve in real time.",
    },
    {
        title: "Dialogue-Based AI Assessments",
        desc: "Instead of MCQs, Veda holds a real conversation with each student to test what they truly understand.",
    },
    {
        title: "Verifiable Portfolios & Scorecards",
        desc: "Every student graduates with a tamper-proof, shareable record of everything they built.",
    },
    {
        title: "Internship & Placement Support",
        desc: "Career planning from Semester 1. Internship connections, placement prep, industry exposure.",
    },
    {
        title: "Faculty Upskilling",
        desc: "Professors supported by AI tools that keep teaching fresh and in line with what companies hire for.",
    },
];

export const COMPANY_VALUES = [
    {
        title: "Verified Portfolios, Not Just CVs",
        desc: "Every AI Prof candidate comes with deployed projects, GitHub code, tested skills, earned certifications. You see proof — not promises.",
    },
    {
        title: "AI-Powered Candidate Matching",
        desc: "Post your job requirements once. AI Prof's system surfaces best-fit candidates based on skills, projects, and Tech Readiness Score.",
    },
    {
        title: "Skill-Based Hiring Tools",
        desc: "Filter by domain, level, tools used, project type, graduation year. Sort by Tech Readiness Score. Shortlist in minutes.",
    },
    {
        title: "Consent-Based, Secure Access",
        desc: "Student data shared with explicit consent. Role-based recruiter access. PDPB-compliant by design.",
    },
    {
        title: "Recruiter Dashboard",
        desc: "Manage postings, review portfolios, track candidates, schedule interviews — all from one AI-powered interface.",
    },
    {
        title: "Interview Scheduling Assist",
        desc: "AI Prof coordinates availability between your team and shortlisted candidates. Faster time-to-offer.",
    },
];

export const MENTORS = [
    {
        name: "Sania Alex",
        role: "Senior Software Engineer",
        company: "Google",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Sania_Alex.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Google.png`,
        linkedin: "https://www.linkedin.com/in/saniaalex/",
    },
    {
        name: "Akshay Kumar",
        role: "Senior SDE",
        company: "Amazon",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Akshay_Kumar.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Amazon.png`,
        linkedin: "https://www.linkedin.com/in/akshaypavan/",
    },
    {
        name: "Vishnu Rudrasamudram",
        role: "Senior Software Engineer - Robot Controls",
        company: "Symbotic",
        domain: "Robotics & AI",
        img: `${process.env.PUBLIC_URL}/mentors/Vishnu_Rudrasamudram.png`,
        logo: `${process.env.PUBLIC_URL}/mentors/Symbotic.png`,
        linkedin: "https://www.linkedin.com/in/vishnuvrs/",
    },
    {
        name: "Sandeep Lamichhane",
        role: "Senior Staff Software Engineer",
        company: "Oracle",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Sandeep_Lamicchane.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Oracle.png`,
        linkedin: "https://www.linkedin.com/in/sandeep-lamichhane-8b700489/",
    },
    {
        name: "Madan Mohan Kuruba",
        role: "Software Engineer II",
        company: "Microsoft",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Madan_Mohan_Kuruba.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Microsoft.png`,
        linkedin: "https://www.linkedin.com/in/madan-mohan-kuruba/",
    },
    {
        name: "Susmitha Kandula",
        role: "Senior Software Engineer",
        company: "Rivian",
        domain: "Embedded Systems",
        img: `${process.env.PUBLIC_URL}/mentors/Susmitha_Kandula.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Rivian.png`,
        linkedin: "https://www.linkedin.com/in/kandulsh5/",
    },
    {
        name: "Indira Emmadi",
        role: "VP - Information Security & Technology",
        company: "State Street",
        domain: "Cybersecurity",
        img: `${process.env.PUBLIC_URL}/mentors/Indira_Emmadi.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/State Street.png`,
        linkedin: "https://www.linkedin.com/in/indira-e-721a5a9/",
    },
    {
        name: "Dinesh Katakam",
        role: "Software Engineer II",
        company: "Microsoft",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Dinesh_Katakam.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Microsoft.png`,
        linkedin: "https://www.linkedin.com/in/dineshkatakam/",
    },
    {
        name: "Ravali Bojja",
        role: "Senior Software Engineer",
        company: "Intuit",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Ravali_Bojja.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Intuit.png`,
        linkedin: "https://www.linkedin.com/in/ravalivr7/",
    },
    {
        name: "Tom Peter",
        role: "Software Engineer - Networking & Storage",
        company: "NetApp",
        domain: "Cloud / Networking",
        img: `${process.env.PUBLIC_URL}/mentors/Tom_Peter.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Netapp.png`,
        linkedin: "https://www.linkedin.com/in/tompeterv10/",
    },
];

export const TESTIMONIALS = [
    {
        name: "Prof. Elena Marston",
        role: "AI Curriculum, Cambridge",
        quote: "The integration of AI into assessment at this level is genuinely impressive. AI Prof is ahead of most institutions I've worked with globally.",
        img: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?crop=faces&fit=crop&w=400&h=400&q=80",
    },
    {
        name: "Vikram Anand",
        role: "VP Engineering, Razorpay",
        quote: "We've been reviewing AI Prof's model closely. The portfolio-based hiring pipeline is the future. We're looking forward to sourcing talent this way.",
        img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=faces&fit=crop&w=400&h=400&q=80",
    },
    {
        name: "Aarav Krishnan",
        role: "B.Tech CSE, NIT Warangal",
        quote: "I always thought I needed to be from an IIT to get a good tech job. AI Prof showed me that what companies want is proof of work and now I have it.",
        img: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?crop=faces&fit=crop&w=400&h=400&q=80",
    },
    {
        name: "Ananya Bhat",
        role: "Sr Eng Manager, Flipkart",
        quote: "What AI Prof is doing is exactly what the Indian tech industry has been asking for. Students who build, not just study.",
        img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?crop=faces&fit=crop&w=400&h=400&q=80",
    },
];

export const QUIZ_QUESTIONS = [
    {
        q: "What excites you most about tech?",
        options: [
            { k: "A", v: "Building products people use" },
            { k: "B", v: "Solving problems with data" },
            { k: "C", v: "Making systems secure" },
            { k: "D", v: "Training AI models" },
        ],
    },
    {
        q: "How comfortable are you with coding today?",
        options: [
            { k: "A", v: "Never written code" },
            { k: "B", v: "Basic HTML/Python" },
            { k: "C", v: "Built a small project" },
            { k: "D", v: "Comfortable with multiple languages" },
        ],
    },
    {
        q: "What kind of company do you want to work at?",
        options: [
            { k: "A", v: "Big tech (Google, Microsoft)" },
            { k: "B", v: "Startup" },
            { k: "C", v: "MNC in India" },
            { k: "D", v: "Start my own company" },
        ],
    },
    {
        q: "What's your biggest career goal in 3 years?",
        options: [
            { k: "A", v: "High salary package" },
            { k: "B", v: "Work abroad" },
            { k: "C", v: "Build something meaningful" },
            { k: "D", v: "Become a specialist in one field" },
        ],
    },
    {
        q: "How much time can you give daily?",
        options: [
            { k: "A", v: "30 mins" },
            { k: "B", v: "1 hour" },
            { k: "C", v: "2–3 hours" },
            { k: "D", v: "Full time" },
        ],
    },
    {
        q: "Which sounds most like you?",
        options: [
            { k: "A", v: "I like building things with my hands" },
            { k: "B", v: "I like understanding patterns" },
            { k: "C", v: "I like breaking things to find flaws" },
            { k: "D", v: "I like teaching machines to think" },
        ],
    },
];

// ============================================================
// COMPONENT UI COPY — user-facing strings relocated from JSX
// ============================================================

export const READINESS_QUIZ = {
    poweredBy: "Powered by AI Prof",
    headingLine1: "Not Sure Which Domain?",
    headingLine2: "Let AI Decide.",
    description:
        "Answer 6 quick questions and we'll map your interests, strengths, and goals to the domain where you'll thrive most. Takes 2 minutes.",
    startCta: "Find My Domain",
    questionPrefix: "Question",
    questionConnector: "of",
    restart: "Restart",
    loading: "AI is analyzing your answers...",
    recommendedLabel: "Your Recommended Domain",
    nextStepsLabel: "Your next 3 steps",
    claimCta: "Claim My Spot",
    retake: "Retake",
};

export const TESTIMONIALS_SECTION = {
    eyebrow: "Heard From The People Who Matter",
    headingLine1: "Students, professors, engineers,",
    headingLine2: "and academic leaders",
    headingHighlight: " in their own words.",
    comingSoon: "Student Success Story — Coming Soon",
};

export const COOKIE_BANNER = {
    heading: "Cookies, Terms & Conditions 🍪",
    messagePrefix: "We use cookies to improve your experience on AI.Prof. By clicking",
    acceptInline: " “OK, I Accept”",
    messageMiddle: ", you agree to our ",
    termsLinkLabel: "Terms & Conditions",
    messageConnector: " and ",
    cookieLinkLabel: "Cookie Policy",
    messageSuffix: ".",
    accept: "OK, I Accept",
    decline: "Decline",
};

// ============================================================
// RE-EXPORTS FROM ORGANIZED DATA FILES
// ============================================================

// Home page data
export {
    TRUSTED_COMPANIES,
    HERO_SECTION,
    STATS_QUOTE_SECTION,
    COMPARISON_SECTION,
    SKILL_GAP_SECTION,
    PROGRAMMES_SECTION,
    MENTORS_SECTION,
    FAQ_SECTION,
} from "./home";

// About / Leadership page data
export { LEADERSHIP_PAGE } from "./leadership-data";

// Pages data (Programmes, Universities, Companies, Careers)
export {
    CS_PROGRAMME_HERO,
    CS_PROGRAMME_STATS,
    CS_PROGRAMME_OFFERINGS,
    CS_PROGRAMME_YEARS,
    CS_VEDA_SECTION,
    CS_PLACEMENT_SECTION,
    CS_CMU_SECTION,
    CS_ELIGIBILITY_SECTION,
    CS_FAQ,
    CS_CTA,
    DOMAIN_IMAGES,
    UNIVERSITIES_HERO,
    UNIVERSITIES_CAPABILITIES,
    UNIVERSITIES_FEATURES_SECTION,
    UNIVERSITIES_CTA,
    COMPANIES_HERO,
    COMPANIES_FLOW,
    COMPANIES_FEATURES_SECTION,
    COMPANIES_CTA,
    CAREERS_HERO,
    CAREERS_ROLES,
    CAREERS_PERKS,
    CAREERS_WHY_SECTION,
    CAREERS_OPEN_APPLICATION,
} from "./pages";
